import { useState } from 'react';
import { format } from 'date-fns';
import { AlertTriangle, ChevronDown, ChevronRight, Edit, Trash, LoaderCircle, Plus } from 'lucide-react';
import { Badge } from '@radix-ui/themes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { AlertSeverityType } from '@/types/alerts';
import { getAlertSeverityColorClasses } from '@/utils/alert-utils';
import { formatThresholdValue } from '@/utils/format-utils';
import { EmptyState } from '@/components/ui/empty-state';
import { GetAlertRulesApiResponse } from '@/services';

type AlertRule = GetAlertRulesApiResponse['data']['items'][0];

export type AlertRuleListProps = {
  rules: AlertRule[];
  isLoading: boolean;
  isFetching?: boolean;
  onEdit: (rule: AlertRule) => void;
  onDelete: (ruleId: string) => void;
  onToggleStatus: (rule: AlertRule) => void;
  onCreateNew: () => void;
  isActionLoading?: boolean;
};

export const AlertRuleList = ({
  rules,
  isLoading,
  onEdit,
  onDelete,
  onToggleStatus,
  onCreateNew,
  isActionLoading = false,
}: AlertRuleListProps) => {
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;

  // Pagination
  const totalPages = Math.ceil(rules.length / itemsPerPage);
  const paginatedRules = rules.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const toggleExpandRule = (ruleId: string) => {
    setExpandedRuleId(expandedRuleId === ruleId ? null : ruleId);
  };

  // Loading skeleton
  if (isLoading) {
    return <AlertRuleListSkeleton />;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex justify-between items-center">
          <CardTitle className="text-lg">Alert Rules ({rules.length})</CardTitle>
          <Button size="sm" onClick={onCreateNew}>
            <Plus className="h-4 w-4 mr-1" />
            New Rule
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Configure when alerts should be triggered for your devices and sensors
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {rules.length === 0 ? (
          <EmptyState
            icon={AlertTriangle}
            title="No alert rules defined"
            description="Create rules to get notified of important events"
            iconClassName="text-amber-500 bg-amber-50 dark:bg-amber-950/30"
            action={<Button onClick={onCreateNew}>Create Your First Rule</Button>}
          />
        ) : (
          <>
            {paginatedRules.map((rule) => (
              <RuleCard
                key={rule.id}
                rule={rule}
                isExpanded={expandedRuleId === rule.id}
                onToggleExpand={toggleExpandRule}
                onEdit={onEdit}
                onDelete={onDelete}
                onToggleStatus={onToggleStatus}
                isActionLoading={isActionLoading}
              />
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pt-3 flex justify-center gap-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="py-2 px-3 text-sm">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Loading skeleton for the alert rule list
const AlertRuleListSkeleton = () => (
  <Card>
    <CardHeader>
      <div className="flex justify-between items-center">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-9 w-24" />
      </div>
      <Skeleton className="h-6 w-80 mt-1" />
    </CardHeader>
    <CardContent>
      {[1, 2, 3].map((i) => (
        <div key={i} className="mb-6 border rounded-lg p-4">
          <div className="flex justify-between">
            <div>
              <Skeleton className="h-5 w-64 mb-2" />
              <Skeleton className="h-4 w-40" />
            </div>
            <div>
              <Skeleton className="h-6 w-16 rounded-full" />
            </div>
          </div>
          <Skeleton className="h-4 w-full mt-4" />
          <div className="flex justify-end mt-3">
            <Skeleton className="h-8 w-20 mr-2" />
            <Skeleton className="h-8 w-20" />
          </div>
        </div>
      ))}
    </CardContent>
  </Card>
);

// Rule Card Component for individual rules
const RuleCard = ({
  rule,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onToggleStatus,
  isActionLoading,
}: {
  rule: AlertRule;
  isExpanded: boolean;
  onToggleExpand: (ruleId: string) => void;
  onEdit: (rule: AlertRule) => void;
  onDelete: (ruleId: string) => void;
  onToggleStatus: (rule: AlertRule) => void;
  isActionLoading: boolean;
}) => {
  const severityColors = getAlertSeverityColorClasses(rule.severity as AlertSeverityType);

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="p-4">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium">{rule.name}</h3>
              <AlertSeverityBadge severity={rule.severity as AlertSeverityType} />
              {rule.status === 'paused' && (
                <Badge radius="full" color="gray" size="1">
                  Paused
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
              {rule.device?.name || 'All Devices'}
              <span className="mx-1">•</span>
              {rule.sensor_type || 'All Sensors'}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Switch
              checked={rule.status === 'active'}
              onCheckedChange={() => onToggleStatus(rule)}
              aria-label={rule.status === 'active' ? 'Disable rule' : 'Enable rule'}
              disabled={isActionLoading}
            />
            <Button variant="ghost" size="sm" onClick={() => onToggleExpand(rule.id)} aria-label="Toggle details">
              {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        <div className="mt-2 text-sm flex items-center">
          <span className={`${severityColors.textLight} ${severityColors.textDark} font-medium`}>
            {rule.condition_type === 'gt' && 'Above'}
            {rule.condition_type === 'lt' && 'Below'}
            {rule.condition_type === 'eq' && 'Equal to'}
            {(rule.condition_type === 'range' || rule.condition_type === 'between') && 'Between'}
            {rule.condition_type === 'outside' && 'Outside'}
          </span>
          <span className="mx-1">threshold</span>
          <span className="font-medium">{formatThresholdValue(rule.threshold_value, rule.condition_type)}</span>
          {rule.duration_seconds && <span className="text-muted-foreground ml-2">for {rule.duration_seconds}s</span>}
        </div>

        {isExpanded && (
          <>
            <div className="mt-3 pt-3 border-t text-sm grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <span className="text-muted-foreground">Description:</span>{' '}
                <span>{rule.description || 'No description'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Cooldown:</span> <span>{rule.cooldown_minutes} minutes</span>
              </div>
              <div>
                <span className="text-muted-foreground">Created:</span>{' '}
                <span>{format(new Date(rule.created_at), 'MMM d, yyyy')}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Last modified:</span>{' '}
                <span>{format(new Date(rule.updated_at), 'MMM d, yyyy')}</span>
              </div>
              {rule.created_by_user && (
                <div>
                  <span className="text-muted-foreground">Created by:</span>{' '}
                  <span>{rule.created_by_user.name}</span>
                </div>
              )}
            </div>

            <div className="mt-4 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                className="mr-2"
                onClick={() => onEdit(rule)}
                disabled={isActionLoading}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 border-red-200"
                onClick={() => onDelete(rule.id)}
                disabled={isActionLoading}
              >
                {isActionLoading ? (
                  <LoaderCircle className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Trash className="h-4 w-4 mr-1" />
                )}
                Delete
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Alert Severity Badge Component
const AlertSeverityBadge = ({ severity }: { severity: AlertSeverityType }) => {
  const label = severity.charAt(0).toUpperCase() + severity.slice(1);
  let color: 'red' | 'orange' | 'yellow' | 'blue' | 'green' | 'gray';

  switch (severity) {
    case 'critical':
      color = 'red';
      break;
    case 'high':
      color = 'orange';
      break;
    case 'medium':
      color = 'yellow';
      break;
    case 'low':
      color = 'blue';
      break;
    case 'info':
      color = 'green';
      break;
    default:
      color = 'gray';
  }

  return (
    <Badge radius="full" color={color} size="1">
      {label}
    </Badge>
  );
};
